/* eslint-disable react/no-unstable-nested-components */
import { Card, Stack, Autocomplete, Grid, TextField } from "@mui/material";
import { DataGrid, gridStringOrNumberComparator } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import CircularProgress from "@mui/material/CircularProgress";
import { getAPICall } from "lib/callApi";
import { INTEGRATION_EVENTS, DATE_FILTER_OPTIONS, FETCH_COMPANY_LIST } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import { displayDate } from "utils/Date";
import MDDatePicker from "components/MDDatePicker";
import moment from "moment";
import { useSelector } from "react-redux";
import { getFilterDates } from "utils/HelperFunctions";
import "styles/snapshot.scss";

function IntegrationEvents() {
  const navigate = useNavigate();
  const loginUserState = useSelector((state) => state.loginUser.loginUserState);
  const [isLoading, setIsLoading] = useState(false);
  const [integrationEventsData, setIntegrationEventsData] = useState([]);
  const [usersKpiCountsData, setUsersKpiCountsData] = useState([]);

  const [dateFilter, setDateFilter] = useState("Today");
  const [customDate, setCustomDate] = useState({
    startDate: moment().format("YYYY-MM-DD HH:mm:ss"),
    endDate: moment().format("YYYY-MM-DD HH:mm:ss"),
  });

  const [selectedCompany, setSelectedCompany] = useState();
  const [companyList, setCompanyList] = useState([]);

  const fetchCompanies = async () => {
    setIsLoading(true);
    try {
      const result = await getAPICall(FETCH_COMPANY_LIST, true);

      if (result?.data?.success) {
        setCompanyList(
          result.data.data.map((company) => ({ id: company.id, name: company?.name }))
        );
      } else if (result?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
    setIsLoading(false);
  };

  const getIntegrationEventsData = async () => {
    if (
      !selectedCompany ||
      !dateFilter ||
      (dateFilter === "Custom" && (!customDate?.startDate || !customDate?.endDate))
    ) {
      setIntegrationEventsData([]);
      return;
    }
    setIsLoading(true);
    try {
      const dates =
        dateFilter !== "Custom"
          ? getFilterDates(dateFilter, loginUserState?.timezone, loginUserState?.fiscalYearStart)
          : {
              startDate: moment
                .tz(customDate.startDate, loginUserState?.timezone)
                .startOf("day")
                .format("YYYY-MM-DD HH:mm:ss"),
              endDate: moment
                .tz(customDate.endDate, loginUserState?.timezone)
                .endOf("day")
                .format("YYYY-MM-DD HH:mm:ss"),
              timezone: loginUserState?.timezone,
            };

      const result = await getAPICall(INTEGRATION_EVENTS, true, {
        ...dates,
        companyId: selectedCompany.id,
      });
      if (result?.data?.success) {
        setIntegrationEventsData(result?.data?.data || []);
      } else if (result?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: "Could not retrieve Integration events data!",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text:
            error?.response?.data?.message ||
            error.message ||
            "Could not retrieve Integration events data!",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  function isNullOrEmpty(str) {
    return str === null || str === "";
  }

  // Custom sorter than puts null/empty values at the bottom of the sort
  const nullHandlingSortComparator = (v1, v2, cellParams1, cellParams2) => {
    if (isNullOrEmpty(v1) || isNullOrEmpty(v2)) {
      if (v1 === v2) return 0;
      const sortModel = cellParams1.api.getSortModel();
      const sortColumn = sortModel.find((sm) => sm.field === cellParams1.field);
      if (sortColumn && sortColumn.sort === "desc") {
        return isNullOrEmpty(v1) ? -1 : 1;
      }
      return isNullOrEmpty(v1) ? 1 : -1;
    }
    return gridStringOrNumberComparator(v1, v2, cellParams1, cellParams2);
  };

  const columns = [
    {
      field: "userName",
      headerName: "User Name",
      headerClassName: "datatable-header",
      minWidth: 250,
      flex: 1,
      sortComparator: nullHandlingSortComparator,
    },
    {
      field: "kpiName",
      headerName: "Kpi Name",
      headerClassName: "datatable-header",
      minWidth: 250,
      flex: 1,
      sortComparator: nullHandlingSortComparator,
    },
    {
      field: "integrationIncreaseCount",
      headerName: "Kpis incremented by integration events",
      headerClassName: "datatable-header",
      minWidth: 150,
      flex: 1,
      sortComparator: nullHandlingSortComparator,
    },
    {
      field: "manualIncreaseCount",
      headerName: "Amount done manually",
      headerClassName: "datatable-header",
      minWidth: 150,
      flex: 1,
      sortComparator: nullHandlingSortComparator,
    },
  ];

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData) {
      navigate("/sign-in");
    }

    fetchCompanies();
  }, []);

  useEffect(() => {
    getIntegrationEventsData();
  }, [selectedCompany, customDate, dateFilter]);

  const formatUserKpiCountRows = () => {
    const userKpiCounts = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const userKpiCount of integrationEventsData?.user_kpi_counts || []) {
      // eslint-disable-next-line no-restricted-syntax
      for (const [kpi, points] of Object.entries(userKpiCount?.kpiIncreaseCounts) || []) {
        userKpiCounts.push({
          userName: userKpiCount?.userName,
          kpiName: kpi,
          integrationIncreaseCount: points[0],
          manualIncreaseCount: points[1],
          id: `${userKpiCount?.userName}-${kpi}`,
        });
      }
    }

    setUsersKpiCountsData(userKpiCounts);
  };

  useEffect(() => {
    formatUserKpiCountRows();
  }, [integrationEventsData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div role="tabpanel" id={`simple-tabpanel-${0}`} aria-labelledby={`simple-tab-${0}`}>
        <MDBox sx={{ pt: 3 }}>
          <Card>
            <MDBox p={3} pb={0} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                Alysio Integration Events
              </MDTypography>
            </MDBox>

            {/* dropdown for filter */}
            <MDBox p={3} pb={0} lineHeight={1}>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                spacing={2}
              >
                <Grid item xs={12} md={4} lg={3} xl={2}>
                  <Autocomplete
                    fullWidth
                    id="company-autocomplete"
                    options={companyList}
                    value={selectedCompany || null}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    onChange={(_, value) => {
                      setSelectedCompany(value);
                    }}
                    renderInput={(params) => <TextField {...params} label="Company" />}
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={2}
                  >
                    {dateFilter === "Custom" && (
                      <Grid item xs={12} md={6} lg={4} xl={3}>
                        <Stack display="flex" flexDirection="row" gap="8px">
                          <MDDatePicker
                            input={{
                              placeholder: "Select start date",
                              label: "Start date",
                              value: displayDate(customDate.startDate),
                            }}
                            value={customDate?.startDate}
                            options={{
                              formatDate: (date) => displayDate(date, "MM-DD-YYYY"),
                              disable: [
                                // eslint-disable-next-line func-names
                                function (date) {
                                  return date.getDay() === 0 || date.getDay() === 6;
                                },
                              ],
                            }}
                            onChange={(value) =>
                              setCustomDate({
                                ...customDate,
                                startDate: moment(value[0]).format("YYYY-MM-DD HH:mm:ss"),
                              })
                            }
                          />
                          <MDDatePicker
                            input={{
                              placeholder: "Select end date",
                              label: "End date",
                              value: displayDate(customDate.endDate),
                            }}
                            value={customDate?.endDate}
                            options={{
                              formatDate: (date) => displayDate(date, "MM-DD-YYYY"),
                              disable: [
                                // eslint-disable-next-line func-names
                                function (date) {
                                  return date.getDay() === 0 || date.getDay() === 6;
                                },
                              ],
                            }}
                            onChange={(value) =>
                              setCustomDate({
                                ...customDate,
                                endDate: moment(value[0]).format("YYYY-MM-DD HH:mm:ss"),
                              })
                            }
                          />
                        </Stack>
                      </Grid>
                    )}

                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        disablePortal
                        disableClearable
                        id="filter-options-autocomplete"
                        value={dateFilter}
                        onChange={(_, filter) => setDateFilter(filter)}
                        options={DATE_FILTER_OPTIONS}
                        renderInput={(params) => <TextField {...params} label="Filter options" />}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </MDBox>

            <MDBox
              sx={{
                width: "100%",
                overflowX: "auto",
                fontSize: "0.875rem",
              }}
            >
              {isLoading ? (
                <Stack alignItems="center">
                  <CircularProgress
                    sx={{
                      position: "fixed",
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2,
                    }}
                    color="info"
                    size="10vh"
                  />
                </Stack>
              ) : (
                ""
              )}

              <DataGrid
                sx={{
                  mt: "1rem",
                  "&.MuiTypography-root": {
                    fontSize: "1rem !important",
                  },
                }}
                rows={usersKpiCountsData}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
              />
            </MDBox>
          </Card>
        </MDBox>
      </div>
    </DashboardLayout>
  );
}

export default IntegrationEvents;
